// frontend/src/components/Product/ProductCard.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CurrencyDisplay from "../UI/CurrencyDisplay";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWhatsapp,
  faWhatsapp as faWhatsappBrand,
} from "@fortawesome/free-brands-svg-icons";
import {
  faCalendarCheck,
  faGlobe,
  faStar,
  faTractor,
  faClock,
  faTag,
  faBox,
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const PRE_ORDER_SIZES = [25, 50];

const ProductCard = ({
  product,
  userType: propUserType,
  farmSize: propFarmSize,
}) => {
  const { user } = useAuth();
  const [imageError, setImageError] = useState(false);

  const userType = propUserType || user?.userType || "customer";
  const farmSize = propFarmSize || user?.farmDetails?.farmSize || "small";
  const isFarmer = userType === "farmer";

  const getDisplayPriceUSD = () => {
    if (product.calculatedPrice?.perUnit) {
      return product.calculatedPrice.perUnit;
    }
    if (product.basePrice?.usd) {
      return product.basePrice.usd;
    }
    return 0;
  };

  const displayPriceUSD = getDisplayPriceUSD();
  const discountPercent = product.calculatedPrice?.discountPercent || 0;

  const getImageSrc = () => {
    if (imageError) return null;
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    if (product.thumbnail) return product.thumbnail;
    return null;
  };

  const imageSrc = getImageSrc();
  const stockStatus =
    product.stockStatus || (product.stock > 0 ? "in_stock" : "out_of_stock");
  const isInStock = stockStatus !== "out_of_stock" && product.stock > 0;

  const hasPreOrder =
    product.isPreOrderAvailable !== false &&
    product.preOrderSizes &&
    product.preOrderSizes.some((size) => PRE_ORDER_SIZES.includes(size));

  const availableSizes = product.availableSizes || [];
  const preOrderSizes = (product.preOrderSizes || []).filter((size) =>
    PRE_ORDER_SIZES.includes(size),
  );
  const showPreOrderBadge = hasPreOrder && (!isInStock || product.stock < 10);

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const message = `Hi Kenali Enterprise, I'm interested in ${product.name}. Can you provide more information?`;
    window.open(
      `https://wa.me/23231030808?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  // Determine badge to show
  const getBadge = () => {
    if (product.featured) {
      return {
        type: "featured",
        icon: faStar,
        label: "Featured",
        className: "badge-featured",
      };
    }
    if (showPreOrderBadge) {
      return {
        type: "preorder",
        icon: faCalendarCheck,
        label: "Pre-Order",
        className: "badge-preorder",
      };
    }
    if (!isInStock && !hasPreOrder) {
      return {
        type: "out-of-stock",
        icon: faExclamationTriangle,
        label: "Out of Stock",
        className: "badge-out-of-stock",
      };
    }
    if (!isInStock && hasPreOrder) {
      return {
        type: "preorder-ready",
        icon: faClock,
        label: "Pre-Order Only",
        className: "badge-preorder-ready",
      };
    }
    return null;
  };

  const badge = getBadge();

  return (
    <motion.div
      className="product-card"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/products/${product.slug}`} className="product-card-link">
        <div className="product-image-wrapper">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.name}
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="product-image-placeholder">
              <FontAwesomeIcon icon={faBox} />
              <span>No Image</span>
            </div>
          )}

          {badge && (
            <span className={`badge ${badge.className}`}>
              <FontAwesomeIcon icon={badge.icon} /> {badge.label}
            </span>
          )}

          <span className="badge badge-origin">
            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
          </span>

          <button
            className="whatsapp-btn"
            onClick={handleWhatsAppClick}
            aria-label="Ask about this product on WhatsApp"
          >
            <FontAwesomeIcon icon={faWhatsappBrand} /> Ask
          </button>
        </div>

        <div className="product-body">
          <h3>{product.name}</h3>
          <div className="product-category">
            {product.category?.replace(/-/g, " ") || "Product"}
          </div>

          <div className="product-price-row">
            <div className="product-price">
              <CurrencyDisplay amountUSD={displayPriceUSD} compact />
              <span className="unit">/{product.unit || "kg"}</span>
              {discountPercent > 0 && (
                <span className="discount-badge">
                  <FontAwesomeIcon icon={faCheckCircle} /> -{discountPercent}%
                </span>
              )}
            </div>
            {discountPercent > 0 && product.basePrice?.usd && (
              <div className="product-original-price">
                <CurrencyDisplay amountUSD={product.basePrice.usd} compact />
              </div>
            )}
          </div>

          {availableSizes.length > 0 && (
            <div className="size-tags">
              {availableSizes.slice(0, 4).map((size) => {
                const isPreOrderSize = preOrderSizes.includes(size);
                return (
                  <span
                    key={size}
                    className={`size-tag ${isPreOrderSize ? "preorder-size" : ""}`}
                  >
                    {size}kg
                    {isPreOrderSize && (
                      <span className="preorder-dot">
                        <FontAwesomeIcon icon={faCalendarCheck} />
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}

          {isFarmer && product.farmerMinimumOrder && (
            <div className="farmer-badge">
              <FontAwesomeIcon icon={faTractor} /> Min bulk:{" "}
              {product.farmerMinimumOrder}kg
            </div>
          )}

          {hasPreOrder && (
            <div className="pre-order-badge">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Available
              {!isInStock && " (Reserve Now)"}
            </div>
          )}

          {stockStatus === "low_stock" && isInStock && (
            <div className="low-stock-warning">
              <FontAwesomeIcon icon={faClock} /> Only {product.stock} left
            </div>
          )}

          {!isInStock && hasPreOrder && (
            <div className="preorder-only-message">
              <FontAwesomeIcon icon={faClock} /> Pre-Order Now - Ships on
              arrival
            </div>
          )}

          <div className="product-actions">
            {!isInStock && hasPreOrder ? (
              <span className="view-details-btn preorder-action">
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Now →
              </span>
            ) : (
              <span className="view-details-btn">
                View Details <FontAwesomeIcon icon={faStar} />
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
